import React, { useEffect, useState, useMemo } from "react";
import { Space, Tooltip, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { sanitizeHtml } from "../../../utils/helpers/sanitizeHtml";
import { openModal, setCurrentPage, setPageSize } from "../../../redux/features/whiteboard/whiteboardSlice";
import { fetchWhiteboardItems, deleteWhiteboardItem, moveWhiteboardItemUp, moveWhiteboardItemDown } from "../../../redux/features/whiteboard/whiteboardAsynkThunk";
import { WhiteboardItem } from "../../../redux/features/whiteboard/types";
import { TableActionButton, TableStyles, TableWrapper, WhiteboardStyledTable } from "../styles";
import StyledModal from "../../../components/CustomModal";
import { FooterButtons, StyledButton } from "../../../components/styles";
import CustomTable from "../../../components/CustomTable";

const WhiteboardTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, loading, currentPage, pageSize, totalCount } = useAppSelector((state) => state.whiteboard);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchWhiteboardItems({ page: currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handleEdit = (record: WhiteboardItem) => {
    dispatch(openModal(record));
  };

  const handleDelete = (id: string) => {
    setItemToDelete(id);
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await dispatch(deleteWhiteboardItem(itemToDelete)).unwrap();
        await dispatch(fetchWhiteboardItems({ page: currentPage, pageSize }));
        setIsDeleteModalVisible(false);
        setItemToDelete(null);
      } catch (error: any) {
        console.error("Failed to delete whiteboard item:", error);
      }
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setItemToDelete(null);
  };

  const handleMoveUp = (id: string) => {
    dispatch(moveWhiteboardItemUp(id));
  };

  const handleMoveDown = (id: string) => {
    dispatch(moveWhiteboardItemDown(id));
  };

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  const handlePageSizeChange = (_: any, size: number) => {
    dispatch(setPageSize(size));
    dispatch(setCurrentPage(1));
  };

  const columns: ColumnsType<WhiteboardItem | any> = useMemo(() => [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (_: unknown, __: unknown, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "ADDED BY",
      dataIndex: "addedBy",
      key: "addedBy",
      width: 120,
    },
    {
      title: "MESSAGE",
      dataIndex: "message",
      key: "message",
      render: (text: string) => <div className="message-cell" dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />,
    },
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
      width: 180,
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: 150,
      render: (_: unknown, record: WhiteboardItem, index: number) => (
        <Space size="small">
          <Tooltip title="Edit">
            <TableActionButton type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <TableActionButton type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
          </Tooltip>
          <Tooltip title="Move Up">
            <TableActionButton type="text" icon={<ArrowUpOutlined />} onClick={() => handleMoveUp(record.id)} disabled={index === 0} />
          </Tooltip>
          <Tooltip title="Move Down">
            <TableActionButton type="text" icon={<ArrowDownOutlined />} onClick={() => handleMoveDown(record.id)} disabled={index === items.length - 1} />
          </Tooltip>
        </Space>
      ),
    },
  ], [currentPage, pageSize, items]);

  return (
    <TableStyles>
      <div>
        {loading && items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <TableWrapper>
            <WhiteboardStyledTable
              dataSource={items}
              columns={columns}
              rowKey="id"
              loading={loading && items.length > 0}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                onChange: handlePageChange,
                showSizeChanger: true,
                onShowSizeChange: handlePageSizeChange,
                showTotal: (total) => `Total ${total} items`
              }}
            />
          </TableWrapper>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalVisible &&
        <StyledModal
          draggable={true}
          title="Confirm Delete"
          open={isDeleteModalVisible}
          centered={true}
          onCancel={cancelDelete}
          width={500}
          footer={
            <FooterButtons>
              <StyledButton onClick={cancelDelete} disabled={loading}>Cancel</StyledButton>
              <StyledButton type="primary" danger loading={loading} onClick={confirmDelete}>
                Delete
              </StyledButton>
            </FooterButtons>
          }
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px", margin: "16px 15px" }}>
            <ExclamationCircleOutlined style={{ fontSize: "22px", color: "#ff4d4f" }} />
            <p style={{ margin: 0, fontSize: "16px" }}>Are you sure you want to delete?</p>
          </div>
        </StyledModal>}
    </TableStyles>
  );
};

export default WhiteboardTable;
